<template>
  <MeasurementCard
    :title="title"
    :info="info"
    :initialShowOnMap="initialShowOnMap"
    :feature="feature"
    :value="value"
    :coloredEntities="coloredEntities"
    :colorScale="colorScale"
    :table="table"
    :sortDetails="true"
    :color="color"
    :colorLegend="{ hideMean: true }"
  />
</template>
<script setup lang="ts">
import { Feature } from '@/pinia/feature'
import MeasurementCard, {
  ColoredEntities,
  Value
} from '../AnalyticsDashboard/components/MeasurementCard.vue'
import { computed, toRefs } from 'vue'
import { Table } from '../AnalyticsDashboard/components/TableDataSource.vue'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import {
  FilterQuery,
  Measurement,
  MeasurementQuery,
  MeasurementResponse,
  PROJECT_STATUSES,
  PROJECT_TYPES,
  ProjectStatus,
  ProjectType
} from '@/api-client/models/project/Contracts'
import { RGBA, colorBetween } from '@/utils/rgba'
import { FeatureFilters, FeatureType } from '@/composables/useFilters'
import { useFetchFeature } from '@/composables/useFetchFeature'
import { calculateEntityIds, calculateEntityType } from '@/fetches'
import { ColorScaleConfig } from '@/utils/colorScale'

type Props = {
  title: string
  info?: string
  status: ProjectStatus
  internal?: boolean
  feature: Feature
  initialShowOnMap?: boolean
  color?: string
  detailRowNames: Record<ProjectType, LocaleString>
  detailHeaders: {
    type: LocaleString
    value: LocaleString
  }
}

const props = withDefaults(defineProps<Props>(), {
  info: undefined,
  color: '#ff0000',
  initialShowOnMap: false
})

type ProjectMeasurements = {
  value?: number
  coloredEntities?: {
    id: string
    value: number
  }[]
}

type ProjectTypes = {
  rows?: Record<ProjectType, number>
}

function calculateProjectFilterQuery(
  filter: FeatureFilters<FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal>
): FilterQuery | null {
  const entityType = calculateEntityType(filter)
  const entityIds = calculateEntityIds(filter)

  const projectTypes = filter.projectTypes
  const budget = {
    from: filter.budget?.from ? filter.budget.from * 1000 : undefined,
    to: filter.budget?.to ? filter.budget.to * 1000 : undefined
  }

  return {
    entityType,
    entityIds,
    projectTypes,
    budget
  }
}

const createQuery =
  (createMeasurements: (filters: FeatureFilters<ProjectAnalysisFeatures>) => Measurement[]) =>
  (filters) => {
    const filter = calculateProjectFilterQuery(filters)
    if (!filter) return undefined

    return {
      filter,
      internal: filters.type === FeatureType.ProjectAnalysisInternal,
      measurements: createMeasurements(filters)
    }
  }

type ProjectAnalysisFeatures =
  | FeatureType.ProjectAnalysisExternal
  | FeatureType.ProjectAnalysisInternal

const { data: measurements, isLoading } = toRefs(
  useFetchFeature<
    ProjectAnalysisFeatures,
    MeasurementQuery,
    MeasurementResponse,
    ProjectMeasurements
  >(
    props.internal ? FeatureType.ProjectAnalysisInternal : FeatureType.ProjectAnalysisExternal,
    'project-analysis-measurements',
    '/core/v1/projects/measurements',
    createQuery(() => [
      ...PROJECT_STATUSES.map((status) => ({ status })),
      ...PROJECT_STATUSES.map((status) => ({ status, splitBy: 'CONSTRUCTION' }))
    ]),
    ({ measurements }): ProjectMeasurements | undefined => {
      const valueMeasurement = measurements.find((m) => m.status === props.status)
      if (!valueMeasurement) return undefined
      const { splits: valueSplits } = valueMeasurement

      const coloredEntitiesMeasurement = measurements.find(
        (m) => m.status === props.status && m.splitBy === 'CONSTRUCTION'
      )
      return {
        value: valueSplits?.[0]?.value !== null ? valueSplits?.[0]?.value : undefined,
        coloredEntities: coloredEntitiesMeasurement?.splits
          ?.map((s) => ({
            id: s.key ?? '',
            value: s.value
          }))
          .filter((s) => s.value !== null) as { id: string; value: number }[]
      }
    }
  )
)

const { data: typeData, isLoading: typeDataLoading } = toRefs(
  useFetchFeature<ProjectAnalysisFeatures, MeasurementQuery, MeasurementResponse, ProjectTypes>(
    props.internal ? FeatureType.ProjectAnalysisInternal : FeatureType.ProjectAnalysisExternal,
    `project-analysis-types.${props.status}`,
    '/core/v1/projects/measurements',
    createQuery((filters) => [
      ...(filters.projectTypes && filters.projectTypes.length > 0
        ? filters.projectTypes
        : PROJECT_TYPES
      ).map((projecttype) => ({
        status: props.status,
        type: projecttype
      }))
    ]),
    ({ measurements }): ProjectTypes | undefined => {
      return {
        rows: measurements.reduce((acc, m) => {
          if (m.type && m.status === props.status) {
            acc[m.type] = m.splits?.[0]?.value ?? 0
          }
          return acc
        }, {} as Record<ProjectType, number>)
      }
    }
  )
)

const colorScale = computed<ColorScaleConfig>(() => ({
  type: 'INTERPOLATE',
  cutoffs: [
    {
      value: 0,
      color: colorBetween(RGBA.fromHex('#ffffff'), RGBA.fromHex(props.color), 0.1).toHex()
    },
    { value: 10, color: props.color }
  ]
}))

const value = computed<Value | undefined>((): Value | undefined =>
  props.color
    ? {
        type: 'number',
        value: measurements.value?.value,
        isLoading: isLoading.value,
        format: {
          format: {
            type: 'intl-number',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }
        }
      }
    : undefined
)

const coloredEntities = computed<ColoredEntities>(
  (): ColoredEntities => ({
    colorings: measurements.value?.coloredEntities,
    enabledEntityLevels: ['COMPLEX']
  })
)

const table = computed<Table>((): Table => {
  return {
    headers: [props.detailHeaders.type, props.detailHeaders.value],
    rows: PROJECT_TYPES.map((key): Table['rows'][number] => [
      {
        type: 'text',
        text: props.detailRowNames[key]
      },
      {
        type: 'number',
        number: typeData.value?.rows?.[key],
        isLoading: typeDataLoading.value,
        format: {
          format: {
            type: 'intl-number',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }
        }
      }
    ]).filter(([, b]) => b.type === 'number' && b.number)
  }
})
</script>
